import { useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { useTenantId } from '@/hooks/useTenantId';
import { useToast } from '@/hooks/use-toast';
import { getUserFriendlyError } from '@/lib/constants/errorMessages';

export interface Division {
  id: string;
  created_by_user_id: string;
  tenant_id: string;
  name: string;
  description: string | null;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface DivisionFormData {
  name: string;
  description?: string | null;
  is_active?: boolean;
}

export function useDivisions(includeInactive = false) {
  const { user } = useAuth();
  const { tenantId } = useTenantId();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  // Fetch divisions by tenant
  const { data = [], isLoading, error, refetch } = useQuery({
    queryKey: ['divisions', tenantId],
    queryFn: async () => {
      if (!user || !tenantId) return [];

      const { data, error } = await supabase
        .from('divisions')
        .select('*')
        .eq('tenant_id', tenantId)
        .order('name', { ascending: true });

      if (error) throw error;
      return (data || []) as Division[];
    },
    enabled: !!user && !!tenantId,
  });

  // Filter based on includeInactive parameter
  const divisions = useMemo(() => {
    if (includeInactive) return data;
    return data.filter(d => d.is_active);
  }, [data, includeInactive]);

  // Create mutation
  const createMutation = useMutation({
    mutationFn: async (formData: DivisionFormData) => {
      if (!user) throw new Error('User not authenticated');
      if (!tenantId) throw new Error('Tenant not found');

      const { data, error } = await supabase
        .from('divisions')
        .insert([{
          ...formData,
          created_by_user_id: user.id,
          tenant_id: tenantId,
        }])
        .select()
        .single();

      if (error) throw error;
      return data as Division;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['divisions', tenantId] });
      toast({
        title: "Success",
        description: "Division created successfully",
      });
    },
    onError: (error: any) => {
      console.error('Error creating division:', error);
      toast({
        title: "Error",
        description: getUserFriendlyError(error),
        variant: "destructive",
      });
    },
  });

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: Partial<DivisionFormData> }) => {
      if (!user) throw new Error('User not authenticated');

      const { data: updated, error } = await supabase
        .from('divisions')
        .update(data)
        .eq('id', id)
        .eq('tenant_id', tenantId)
        .select()
        .single();

      if (error) throw error;
      return updated as Division;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['divisions', tenantId] });
      toast({
        title: "Success",
        description: "Division updated successfully",
      });
    },
    onError: (error: any) => {
      console.error('Error updating division:', error);
      toast({
        title: "Error",
        description: getUserFriendlyError(error),
        variant: "destructive",
      });
    },
  });

  // Custom archive function (soft delete)
  const archiveDivision = async (id: string) => {
    await updateMutation.mutateAsync({ id, data: { is_active: false } });
  };

  return {
    divisions,
    isLoading,
    createDivision: createMutation,
    updateDivision: updateMutation,
    deleteDivision: { mutateAsync: archiveDivision, isPending: updateMutation.isPending },
    refetch,
  };
}
